import { Box, Grid, Heading, Text } from "@chakra-ui/react";
import { useRef, useState } from "react";
import { RichText } from "@/components";
import { KNIT_ALONG, KNIT_ALONG_HEAD } from "@/content";
import { KnitViewer, type KnitViewerHandle } from "./knit/KnitViewer";

const { chapters, courses } = KNIT_ALONG;

const courseRange = (index: number) => {
  const from = chapters[index].from + 1;
  const to = index + 1 < chapters.length ? chapters[index + 1].from : courses;
  return `Courses ${from}–${to}`;
};

export const KnitAlong = () => {
  const viewerRef = useRef<KnitViewerHandle>(null);
  const [activeId, setActiveId] = useState(chapters[0].id);

  return (
    <Grid
      as="section"
      id="knit-along"
      maxW="maxw"
      mx="auto"
      px="pad"
      pb="sec"
      templateColumns={{ base: "minmax(0, 1fr)", xl: "minmax(0, 1.35fr) minmax(0, 0.65fr)" }}
      templateRows={{ xl: "auto 1fr" }}
      templateAreas={{ base: `"head" "viewer"`, xl: `"viewer head" "viewer chapters"` }}
      columnGap="clamp(32px, 4vw, 56px)"
      rowGap="clamp(24px, 3vw, 36px)"
      alignItems="start"
    >
      <Grid gridArea="head" gap="14px">
        <Text variant="eyebrowBrand">{KNIT_ALONG_HEAD.eyebrow}</Text>
        <Heading as="h2" variant="card" fontSize="clamp(1.75rem, 3vw, 2.75rem)">
          {KNIT_ALONG_HEAD.title}
        </Heading>
        <Text variant="body" fontSize="small" maxW="52ch">
          <RichText nodes={KNIT_ALONG_HEAD.lede} />
        </Text>
      </Grid>

      <Box gridArea="viewer" minW={0}>
        <KnitViewer ref={viewerRef} chapters={chapters} onChapterChange={(chapter) => setActiveId(chapter.id)} />
      </Box>

      {/* Beside the video only: stacked under it, the card's own chapter label is enough. */}
      <Box as="nav" gridArea="chapters" display={{ base: "none", xl: "block" }} aria-label={KNIT_ALONG.chaptersLabel}>
        <Grid as="ol" borderTop="1px solid" borderColor="line.hairline">
          {chapters.map((chapter, index) => {
            const isActive = chapter.id === activeId;

            return (
              <Box as="li" key={chapter.id} borderBottom="1px solid" borderColor="line.hairline">
                <Grid
                  as="button"
                  type="button"
                  aria-current={isActive ? "step" : undefined}
                  onClick={() => {
                    setActiveId(chapter.id);
                    viewerRef.current?.seek(chapter.from);
                  }}
                  w="100%"
                  templateColumns="auto minmax(0, 1fr)"
                  columnGap="clamp(14px, 1.6vw, 22px)"
                  py="clamp(14px, 1.6vw, 20px)"
                  textAlign="left"
                  data-group
                >
                  <Box
                    as="span"
                    fontSize="small"
                    color={isActive ? "brand.600" : "warm.600"}
                    transitionProperty="color"
                    transitionDuration="0.3s"
                    transitionTimingFunction="brand"
                    sx={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    {String(index + 1).padStart(2, "0")}
                  </Box>
                  <Box as="span" display="block" minW={0}>
                    <Heading
                      as="span"
                      display="block"
                      fontSize="clamp(1.0625rem, 1.35vw, 1.375rem)"
                      lineHeight={1}
                      color={isActive ? "ink.DEFAULT" : "warm.600"}
                      transitionProperty="color"
                      transitionDuration="0.3s"
                      transitionTimingFunction="brand"
                      _groupHover={{ color: "ink.DEFAULT" }}
                    >
                      {chapter.title}
                    </Heading>
                    <Text as="span" display="block" mt="6px" variant="small">
                      {courseRange(index)}
                    </Text>
                    <Box
                      className="print-expand"
                      display="grid"
                      gridTemplateRows={isActive ? "1fr" : "0fr"}
                      mt={isActive ? "10px" : 0}
                      opacity={isActive ? 1 : 0}
                      transitionProperty="grid-template-rows, opacity, margin-top"
                      transitionDuration="0.4s"
                      transitionTimingFunction="brand"
                    >
                      <Text
                        as="span"
                        display="block"
                        overflow="hidden"
                        variant="body"
                        fontSize="small"
                        lineHeight={1.45}
                      >
                        {chapter.body}
                      </Text>
                    </Box>
                  </Box>
                </Grid>
              </Box>
            );
          })}
        </Grid>
      </Box>
    </Grid>
  );
};
